import { money } from "./policy";
import { profitBase, type CalcContext } from "./rollup";
import type { Estimate, EstimateCalc } from "./types";

export type CheckLevel = "error" | "warn" | "info";

export interface Check {
  id: string;
  level: CheckLevel;
  title: string;
  ok: boolean;
  /** что показать рядом, когда проверка не прошла */
  detail?: string;
}

/** Пять контролей шаблона допускают копеечную погрешность округления. */
const EPS = 0.01;

/**
 * Автоматические проверки перед выпуском.
 *
 * Первые три воспроизводят «пять контрольных нулей» из шаблона. В самой таблице
 * они записаны как `SUM(J25:J105)/3 − J107`: деление на 3 нужно потому, что в
 * диапазон попадают ресурсная строка, итог блока и итог этапа сразу. Это артефакт
 * плоского листа. Здесь дерево настоящее, поэтому проверки прямые — но остаются:
 * Вика по ним сверяет смету, и в выгрузке они должны сойтись так же.
 */
export function runChecks(estimate: Estimate, calc: EstimateCalc, ctx: CalcContext): Check[] {
  const checks: Check[] = [];
  // Сравниваем в копейках: НДС считается по строкам и суммируется, и на
  // целых оборотах медиаплана разница с `net × ставка` бывает ровно копейкой
  // плюс погрешность двоичной дроби — 0,0100000002 > EPS краснело зря.
  const eq = (a: number, b: number) =>
    Math.abs(Math.round(a * 100) - Math.round(b * 100)) <= EPS * 100;

  // 1. Сумма строк равна сумме блока
  const badBlock = calc.blocks.find((b) => {
    if (b.block.enabled === false) return false;
    return !eq(money(b.lines.reduce((s, l) => s + l.net, 0)), b.net);
  });
  checks.push({
    id: "lines-sum-block",
    level: "error",
    title: "Сумма строк равна сумме блока",
    ok: !badBlock,
    detail: badBlock ? `расходится в блоке «${badBlock.block.name}»` : undefined,
  });

  // 2. Сумма блоков равна сумме этапа
  const badStage = calc.stages.find(
    (s) => !eq(money(s.blocks.reduce((acc, b) => acc + b.net, 0)), s.net),
  );
  checks.push({
    id: "blocks-sum-stage",
    level: "error",
    title: "Сумма блоков равна сумме этапа",
    ok: !badStage,
    detail: badStage ? `расходится в этапе «${badStage.stage.name}»` : undefined,
  });

  // 3. Сумма этапов равна итогу проекта
  const stagesNet = money(calc.stages.reduce((s, x) => s + x.net, 0));
  checks.push({
    id: "stages-sum-total",
    level: "error",
    title: "Сумма этапов равна итогу проекта",
    ok: eq(stagesNet, calc.net),
    detail: eq(stagesNet, calc.net) ? undefined : `этапы ${stagesNet}, итог ${calc.net}`,
  });

  // 4. НДС пересчитывается из базы
  const expectedVat = money(calc.net * ctx.scheme.vatRate);
  checks.push({
    id: "vat-recalc",
    level: "error",
    title: `НДС ${Math.round(ctx.scheme.vatRate * 100)}% сходится с базой`,
    ok: eq(expectedVat, calc.vat),
    detail: eq(expectedVat, calc.vat) ? undefined : `ожидали ${expectedVat}, в смете ${calc.vat}`,
  });

  // 5. Прибыль равна выручке без НДС минус себестоимость
  const revenue = profitBase(calc);
  const expectedProfit = money(revenue - calc.cost);
  checks.push({
    id: "profit-formula",
    level: "error",
    title: "Прибыль равна выручке минус себестоимость",
    ok: eq(expectedProfit, calc.profit),
    detail: eq(expectedProfit, calc.profit)
      ? undefined
      : `ожидали ${expectedProfit}, в смете ${calc.profit}`,
  });

  // 6. Все строки имеют цену — иначе итог является нижней границей
  // Выключенный блок в итог не идёт — и его строки без цены итог не делают
  // нижней границей. Тот же фильтр, что у выгрузки: иначе экран красный, а
  // файл без пометки «неполный».
  const missing = calc.blocks
    .filter((b) => b.block.enabled !== false)
    .flatMap((b) =>
      b.lines.filter((l) => l.rateMissing).map((l) => `${b.block.name} → ${l.line.name}`),
    );
  checks.push({
    id: "rates-resolved",
    level: "error",
    title: "У всех строк есть цена",
    ok: missing.length === 0,
    detail: missing.length
      ? `итог — нижняя граница, нет ставки: ${missing.slice(0, 3).join("; ")}${
          missing.length > 3 ? ` и ещё ${missing.length - 3}` : ""
        }`
      : undefined,
  });

  // 6а. Количество не бывает отрицательным. Экран такое не принимает, ядро
  // считает всё, что дали, а дают ему не только с экрана. Единственный из
  // контролей выше, который смотрит на данные, а не на собственную арифметику.
  const negative = calc.blocks
    .filter((b) => b.block.enabled !== false)
    .flatMap((b) =>
      b.lines.filter((l) => l.line.qty < 0).map((l) => `${b.block.name} → ${l.line.name}`),
    );
  checks.push({
    id: "qty-nonnegative",
    level: "error",
    title: "Количества не отрицательные",
    ok: negative.length === 0,
    detail: negative.length ? `меньше нуля: ${negative.slice(0, 3).join("; ")}` : undefined,
  });

  // 7. У внешних исполнителей заведён расход. Ноль у наших специалистов —
  // значение, а не пропуск; дыра только там, где исполнитель внешний.
  const active = calc.blocks.filter((b) => b.block.enabled !== false).flatMap((b) => b.lines);
  const missingCost = active.filter((l) => l.costSource === "missing");
  checks.push({
    id: "cost-entered",
    level: "warn",
    title: "У внешних исполнителей заведён расход",
    ok: missingCost.length === 0,
    detail: missingCost.length
      ? `без расхода: ${missingCost
          .slice(0, 3)
          .map((l) => l.line.name)
          .join("; ")}${missingCost.length > 3 ? ` и ещё ${missingCost.length - 3}` : ""}. ` +
        "Рентабельность предварительная"
      : undefined,
  });

  // 7а. Цены-ориентиры из архива. Не ошибка — рабочее состояние сметы на
  // раннем этапе, но выпускать её клиенту с неподтверждёнными закупками
  // нельзя: это и есть та самая цифра, которую потом никто не вспомнит
  // проверить.
  // Строка, которую уже считают от счёта подрядчика, ориентиром не живёт:
  // цену назвал подрядчик, уточнять у него нечего.
  const estimated = active.filter(
    (l) =>
      l.line.priceEstimated && l.line.unitPrice && !(l.line.contractor && l.costSource === "exact"),
  );
  if (estimated.length) {
    checks.push({
      id: "price-estimated",
      level: "warn",
      title: "Цены закупок подтверждены",
      ok: false,
      detail: `по архиву, уточните у подрядчика: ${estimated
        .slice(0, 3)
        .map((l) => l.line.name)
        .join("; ")}${estimated.length > 3 ? ` и ещё ${estimated.length - 3}` : ""}`,
    });
  }

  // 7б. Строка в убыток: расход выше цены. В архиве такое встречается — иногда
  // это осознанный демпинг, но чаще описка, поэтому предупреждаем.
  const loss = active.filter((l) => l.cost > 0 && l.net > 0 && l.cost > l.net);
  if (loss.length) {
    checks.push({
      id: "line-loss",
      level: "warn",
      title: "Есть строки в убыток",
      ok: false,
      detail: loss
        .slice(0, 3)
        .map((l) => `${l.line.name}: расход выше цены`)
        .join("; "),
    });
  }

  // 8. Факт против плана типа проекта; у типа без плана проверки нет
  if (typeof calc.planMargin === "number") {
    const ok = calc.margin >= calc.planMargin;
    checks.push({
      id: "plan-margin",
      level: "warn",
      title: "Рентабельность не ниже плана",
      ok,
      detail: ok ? undefined : `план ${pct(calc.planMargin)}, факт ${pct(calc.margin)}`,
    });
  }

  // 9. Целевая рентабельность конкретного проекта, если задана
  if (typeof estimate.targetMargin === "number") {
    const ok = calc.margin >= estimate.targetMargin;
    checks.push({
      id: "target-margin",
      level: "warn",
      title: "Рентабельность не ниже целевой по проекту",
      ok,
      detail: ok ? undefined : `цель ${pct(estimate.targetMargin)}, факт ${pct(calc.margin)}`,
    });
  }

  // 10. Крупные блоки — на усиленную проверку (ТЗ п.8.7)
  const big = calc.blocks.filter((b) => b.block.enabled !== false && b.net >= BIG_BLOCK_THRESHOLD);
  if (big.length) {
    checks.push({
      id: "big-blocks",
      level: "info",
      title: "Крупные блоки объяснены конкретным результатом",
      ok: false,
      detail: `проверить вручную: ${big.map((b) => b.block.name).join("; ")}`,
    });
  }

  return checks;
}

/** Порог «крупной суммы» из ТЗ п.8.7. Настраивается администратором. */
export const BIG_BLOCK_THRESHOLD = 1_000_000;

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}
